import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, interval, Subscription, switchMap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TimePipe } from './time.pipe';
import { Project } from '../models/project';
import { DatePipe } from '@angular/common';
import { TimeEntry } from '../models/time-entry';
import { TimesheetService } from '../services/time-sheet.service';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmComponent } from '../dialogs/confirm/confirm.component';
import { DomSanitizer } from '@angular/platform-browser';
import { CommunityAuthService } from '../services/community-auth.service';
import { UIPreference } from '../models/ui-preferences';

export interface TimerDialogData {
  startTime: number; // Initial start time in seconds
  project: Project;
  day: string;
  weekStart: string;
  checkForAutoSave: Function;
}

export interface TimerDialogResult {
  elapsedTime: number;
  displayHours: string;
  notes: string;
}

@Component({
  selector: 'app-timer-dialog',
  templateUrl: './timer.component.html',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule,
    DragDropModule,
    TimePipe,
    DatePipe
  ],
  styleUrls: ['./timer.component.scss']
})
export class TimerComponent implements OnInit, OnDestroy {
  elapsedTime = 0;
  displayHours = '0.0';
  notesControl = new FormControl('');
  private timerSubscription!: Subscription;
  startDate!: Date;
  startHoursDisplay!: string;
  saveOnce = false;
  lastSaved?: Date;
  latestNotes: string | null = '';
  notesSubscription?: Subscription;
  userSubscr?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<TimerComponent, TimerDialogResult>,
    public dialog: MatDialog,
    public santizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: TimerDialogData,
    private timesheetService: TimesheetService,
    private auth: CommunityAuthService
  ) { }

  ngOnInit(): void {
    this.elapsedTime = this.data.startTime || 0;
    this.latestNotes = this.data.project.notes[this.data.day];
    this.notesControl.setValue(this.latestNotes);
    this.notesSubscription = this.notesControl.valueChanges.pipe(debounceTime(1000)).subscribe(_ => this.initiateSave());
    this.startTimer();
  }

  // ngAfterViewInit() {
  //   this.userSubscr = this.auth.isImpersonated
  //     .pipe(switchMap(_ => this.userService.loadCurrentUser()))
  //     .subscribe(user => {
  //       console.log("We've swapped the user");
  //       const uiPreference = user.userUIPreference?.data ? JSON.parse(user.userUIPreference.data) : new UIPreference();
  //       // console.log("UI Preferences", uiPreference, user.userUIPreference);
  //       this.currentUser = user;
  //       const roles = user.userRoles.map(ur => ur.role.roleName.toLowerCase());
  //       this.permissions.canImpersonate = roles.includes("admin") || this.auth.isImpersonating || false;
  //       this.permissions.fullAccess = roles.includes("fullaccess");
  //       this.permissions.hasReports = !!user.reports.length;
  //       this.uiPrefChangedSubject.next(uiPreference);
  //       this.loading = false;
  //     });
  // }

  startTimer(): void {
    this.startDate = new Date();
    this.startHoursDisplay = (Math.round((this.data.startTime / 3600) * 10) / 10).toFixed(1);
    this.timerSubscription = interval(1000).subscribe(() => {
      this.elapsedTime = (new Date().getTime() - this.startDate.getTime()) / 1000 + this.data.startTime;
      //this.elapsedTime++;
      const newDisplayHours = (Math.round((this.elapsedTime / 3600) * 10) / 10).toFixed(1);
      if (this.displayHours != newDisplayHours || this.saveOnce) {
        this.latestNotes = this.notesControl.value;
        this.displayHours = newDisplayHours;
        this.initiateSave();
      }
    });

    // const { day, startTime, project } = this.data;
    // if (!(this.uiPreference?.timesheet?.areTimersPersisted ?? true)) return;
    // const timers = this.uiPreference?.timesheet?.persistedTimers;
    // if (!timers || !timers[day] || !timers[day][project.ui.virtualId]) {
    //   console.log("Adding timer for ", day, project.ui.virtualId, " at ", startHours,);
    // }
  }

  private initiateSave() {
    const entry = new TimeEntry();
    entry.notes = this.notesControl.value || '';
    entry.recordedTime = parseFloat(this.displayHours);
    entry.projectId = this.data.project.id;
    entry.dateEntry = this.data.day;
    entry.timeTypeId = this.data.project.ui.timeTypeId;
    this.timesheetService.updateTimesheet([entry], this.data.weekStart, false).subscribe(s => this.lastSaved = new Date());
  }

  resetTimer(): void {
    this.startDate = new Date();
    this.elapsedTime = this.data.startTime;
    this.displayHours = (Math.round((this.elapsedTime / 3600) * 10) / 10).toFixed(1);
    this.notesControl.setValue(this.data.project.notes[this.data.day]);
    this.initiateSave();
  }

  accept(): void {
    this.initiateSave();
    this.dialogRef.close({
      elapsedTime: this.elapsedTime,
      displayHours: this.displayHours,
      notes: this.notesControl.value || '',
    });
  }

  openHelp() {
    const message = this.santizer.bypassSecurityTrustHtml(`
    <div style='font-size: 12pt;'>
    This timer automatically <b>saves after every 6 minutes (0.1 hours)</b> of elapsed time or when you click 'Save and Close.' Clicking 'Reset' or 'Cancel' will revert any autosaves done by the timer.
        <ul>
        <li>It can run while you continue using TKS or other applications.</li>
        <li>It will run up to the length of your login session.</li>
        <li>Login sessions are limited to <b>8 hours</b>. Please be sure to take this into account when timing your work.</li>
        </ul>
    </div>
    `);
    this.dialog.open(ConfirmComponent, { data: { title: 'Timer Help', message, dismissLabel: "Done", isDismissOnly: true } });
  }

  cancel(): void {
    this.elapsedTime = this.data.startTime;
    this.displayHours = (Math.round((this.data.startTime / 3600) * 10) / 10).toFixed(1);
    this.notesControl.setValue(this.data.project.notes[this.data.day]);
    this.initiateSave();
    this.dialogRef.close({
      elapsedTime: this.data.startTime,
      displayHours: this.displayHours,
      notes: this.data.project.notes[this.data.day]
    });
  }

  ngOnDestroy(): void {
    this.timerSubscription.unsubscribe();
    this.notesSubscription?.unsubscribe();
    this.userSubscr?.unsubscribe();
  }
}
